import { LocalDeck,Card } from '../types';

// Encode a deck to a URL-safe string
export const encodeDeck = (deck: Partial<LocalDeck>): string => {
  try {
    const deckData = {
      name: deck.name || 'Imported Deck',
      avatar: deck.avatar || '',
      spellbook: deck.spellbook || [],
      atlas: deck.atlas || []
    };
    
    const jsonString = JSON.stringify(deckData);
    const base64 = btoa(jsonString);
    
    // Make URL-safe by replacing characters
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  } catch (error) {
    console.error('Error encoding deck:', error);
    throw new Error('Failed to encode deck');
  }
};

// Decode a URL-safe string back to a deck
export const decodeDeck = (encodedDeck: string): Partial<LocalDeck> => {
  try {
    // Restore base64 padding and characters
    let base64 = encodedDeck.replace(/-/g, '+').replace(/_/g, '/');
    
    // Add padding if needed
    while (base64.length % 4) {
      base64 += '=';
    }
    
    const jsonString = atob(base64);
    const deckData = JSON.parse(jsonString);
    
    // Validate the decoded data structure
    if (!deckData || typeof deckData !== 'object') {
      throw new Error('Invalid deck data structure');
    }
    
    return {
      name: deckData.name || 'Imported Deck',
      avatar: deckData.avatar || '',
      spellbook: Array.isArray(deckData.spellbook) ? deckData.spellbook : [],
      atlas: Array.isArray(deckData.atlas) ? deckData.atlas : []
    };
  } catch (error) {
    console.error('Error decoding deck:', error);
    throw new Error('Invalid deck URL format');
  }
};

// Generate a shareable URL for a deck
export const generateShareableURL = (deck: Partial<LocalDeck>, baseURL?: string): string => {
  const encoded = encodeDeck(deck);
  const base = baseURL || window.location.origin + window.location.pathname;
  return `${base}?deck=${encoded}`;
};

// Parse URL parameters to extract deck data
export const parseURLForDeck = (): Partial<LocalDeck> | null => {
  if (typeof window === 'undefined') return null;
  
  const params = new URLSearchParams(window.location.search);
  const deckParam = params.get('deck');
  
  if (!deckParam) return null;
  
  try {
    return decodeDeck(deckParam);
  } catch (error) {
    console.error('Failed to parse deck from URL:', error);
    return null;
  }
};

// Validate that a deck has the minimum required structure
export const isValidDeck = (deck: unknown): deck is Partial<LocalDeck> => {
  if (!deck || typeof deck !== 'object' || deck === null) {
    return false;
  }

  const d = deck as Record<string, unknown>;
  
  return (
    (typeof d.name === 'string' || d.name === undefined) &&
    (typeof d.avatar === 'string' || d.avatar === undefined) &&
    (Array.isArray(d.spellbook) || d.spellbook === undefined) &&
    (Array.isArray(d.atlas) || d.atlas === undefined)
  );
};


/**
 * Extracts the deck id from a Curiosa deck URL.
 * Example: https://curiosa.io/decks/cmip2vh9y00kal204778yzvbd -> cmip2vh9y00kal204778yzvbd
 */
export const parseCuriosaDeckId = (input: string): string | null => {
  try {
    const trimmed = input.trim();

    // allow raw id paste
    if (/^[a-z0-9]{10,}$/i.test(trimmed) && !trimmed.includes("/")) return trimmed;

    const url = new URL(trimmed);
    if (url.hostname !== "curiosa.io") return null;

    const parts = url.pathname.split("/").filter(Boolean);
    if (parts[0] !== "decks") return null;

    return parts[1] ?? null;
  } catch {
    return null;
  }
};

export const fetchCuriosaDeck = async (deckId: string, cards: Card[]) => {

  const cleanDeckName = (s: unknown): string | null => {
    if (typeof s !== "string") return null;
    const t = s.replace(/\s+/g, " ").trim().replace(/\.\s*$/, ""); // remove final dot
    return t ? t : null;
  };

  const base = typeof window !== "undefined"
    ? (window.location.hostname === "localhost" ? "http://localhost:8787" : window.location.origin)
    : "";


  const res = await fetch(
    `${base}/api/curiosa-deck?id=${encodeURIComponent(deckId)}`
  );

  if (!res.ok) {
    const txt = await res.text();
    throw new Error(`Curiosa fetch failed (${res.status}): ${txt.slice(0, 120)}`);
  }

  const wrapped = await res.json();
  const payload = wrapped.deck;

  // tRPC batch payload
  const deckRows = payload?.[0]?.result?.data?.json ?? [];

  const bySlug = new Map(cards.map((c) => [c.slug, c]));
  const byName = new Map(cards.map((c) => [c.name.toLowerCase(), c]));

  const resolveToLocalSlug = (preferredSlug: string | null, row: any): string | null => {
    // 1) Best case: Curiosa variant slug exists in our local cards
    if (preferredSlug && bySlug.has(preferredSlug)) return preferredSlug;

    // 2) Fallback: match by card name (Curiosa gives canonical name)
    const nm = String(row?.card?.name ?? "").toLowerCase();
    const local = nm ? byName.get(nm) : undefined;
    if (local?.slug) return local.slug;

    // 3) Last resort: keep preferred (may become "other")
    return preferredSlug;
  };


  // -----------------------------
  // Variant preference logic
  // -----------------------------

  const SET_PRIORITY: Record<string, number> = {
    got: 50,
    art: 40,
    bet: 30,
    alp: 20,
    pro: 0,
  };

  const getSetCode = (slug: string) => slug.split("-")[0] || "";

  const pickPreferredVariantSlug = (row: any): string | null => {
    const candidates: any[] = [
      ...(row?.card?.variants ?? []),
      row?.variant,
    ].filter(Boolean);

    if (!candidates.length) return null;

    const deckSet = row?.variant?.slug
      ? getSetCode(row.variant.slug)
      : "";

    const isAvatar =
      String(row?.card?.type ?? "").toLowerCase() === "avatar";

    const score = (v: any) => {
      const slug = String(v?.slug ?? "");
      const finish = String(v?.finish ?? "").toLowerCase();
      const product = String(v?.product ?? "");
      const set = getSetCode(slug);

      let s = 0;

      // 1) Non-foil / Standard
      if (finish === "standard") s += 100;
      if (finish === "foil") s -= 40;
      if (slug.endsWith("-s")) s += 30;
      if (slug.endsWith("-f")) s -= 10;

      // 2) Set preference
      s += SET_PRIORITY[set] ?? 10;

      // 3) Prefer same set as deck entry
      if (deckSet && set === deckSet) s += 15;

      // 4) Avatar-specific rule:
      // prefer Preconstructed (pd-s) over Booster (b-s)
      if (isAvatar && product === "Preconstructed_Deck") s += 35;

      // 5) Penalties
      if (set === "pro") s -= 40;

      return s;
    };

    const best = candidates.reduce((a, b) =>
      score(b) > score(a) ? b : a
    );

    return best?.slug ?? null;
  };


  // -----------------------------
  // Build deck lists
  // -----------------------------

  const spellbook: string[] = [];
  const atlas: string[] = [];

  for (const row of deckRows) {
    const qty = row?.quantity ?? 1;
    const slug = resolveToLocalSlug(pickPreferredVariantSlug(row), row);
    if (!slug) continue;

    const type = String(row?.card?.type ?? "").toLowerCase();
    const isSite = type === "site";

    for (let i = 0; i < qty; i++) {
      (isSite ? atlas : spellbook).push(slug);
    }
  }


  // -----------------------------
  // Avatar (single object, not array)
  // -----------------------------

  const avatarRow = payload?.[1]?.result?.data?.json ?? null;
  const avatar = avatarRow
  ? resolveToLocalSlug(pickPreferredVariantSlug(avatarRow), avatarRow) ?? ""
  : "";

  const name = cleanDeckName(wrapped?.name) ?? "Curiosa Deck";

  return {
    name,
    avatar,
    spellbook,
    atlas,
  };
};
